import {
  ButtonV3,
  type ButtonV3Props,
  LayoutGrid,
  TypographyV3,
  type TypographyV3Props,
} from '@jouzen/ecom-components';
import { cva, cx, type VariantProps } from 'class-variance-authority';
import { useTranslations } from 'next-intl';
import type { ComponentPropsWithoutRef, JSX, SVGProps } from 'react';
import type { MessageKey } from 'types/LocaleMessageKey';

import Image from '../Image';
import type { MotionProps } from '../Motion';
import Motion from '../Motion';

const twoColTextBlockVar = cva('flex flex-col justify-center', {
  variants: {
    smBreakTextPos: {
      top: null,
      bottom: null,
    },
    mdBreakTextPos: {
      left: null,
      right: null,
    },
  },
  compoundVariants: [
    {
      smBreakTextPos: 'top',
      mdBreakTextPos: 'left',
      class: 'col-main row-start-1 md:col-end-12 xxl:col-end-11',
    },
    {
      smBreakTextPos: 'bottom',
      mdBreakTextPos: 'left',
      class: 'col-main row-start-2 md:col-end-12 md:row-start-1 xxl:col-end-11',
    },
    {
      smBreakTextPos: 'top',
      mdBreakTextPos: 'right',
      class: 'col-main row-start-1 md:col-start-14',
    },
    {
      smBreakTextPos: 'bottom',
      mdBreakTextPos: 'right',
      class: 'col-main row-start-2 md:col-start-14 md:row-start-1',
    },
  ],
});

const twoColImageBlockVar = cva('flex flex-col justify-center', {
  variants: {
    smBreakTextPos: {
      top: null,
      bottom: null,
    },
    mdBreakTextPos: {
      left: null,
      right: null,
    },
  },
  compoundVariants: [
    {
      smBreakTextPos: 'top',
      mdBreakTextPos: 'left',
      class:
        'col-main row-start-2 md:col-start-13 md:col-end-main md:row-start-1',
    },
    {
      smBreakTextPos: 'bottom',
      mdBreakTextPos: 'left',
      class: 'col-main row-start-1 md:col-start-13 md:col-end-main',
    },
    {
      smBreakTextPos: 'top',
      mdBreakTextPos: 'right',
      class:
        'col-main row-start-2 md:col-start-main md:col-end-13 md:row-start-1',
    },
    {
      smBreakTextPos: 'bottom',
      mdBreakTextPos: 'right',
      class: 'col-main row-start-1 md:col-start-main md:col-end-13',
    },
  ],
});

type TwoColTextVarProps = VariantProps<typeof twoColTextBlockVar>;
type TwoColImageVarProps = VariantProps<typeof twoColImageBlockVar>;
type VarProps = TwoColTextVarProps & TwoColImageVarProps;

type Icon = (props: SVGProps<SVGSVGElement>) => JSX.Element;
export interface TypographyProps extends Omit<TypographyV3Props, 'children'> {
  readonly label: MessageKey;
}

export interface TextColProps {
  titleProps?: TypographyProps;
  buttonProps?: ButtonProps;
  paragraphs: {
    icon?: Icon;
    textProps: TypographyProps;
    subtextProps?: TypographyProps;
    iconClassName?: string;
  }[];
  textColClassName?: string;
  iconSize?: number;
  iconTextContainerClassName?: string;
}
export interface ButtonProps extends Omit<ButtonV3Props, 'children'> {
  readonly href?: string;
  readonly label: MessageKey;
  readonly siteName?: string;
}

interface TwoColumnModuleProps extends VarProps {
  readonly imageCol: {
    localizedAlt: MessageKey;
    motionProps: MotionProps;
    src: string;
  };
  readonly textCol: TextColProps;
  readonly layoutGridClassName?: string;
}

const TwoColumnModule = ({
  imageCol,
  smBreakTextPos = 'bottom',
  mdBreakTextPos = 'left',
  textCol,
  layoutGridClassName,
  ...props
}: TwoColumnModuleProps & ComponentPropsWithoutRef<'section'>) => {
  const t = useTranslations();
  const { titleProps, buttonProps } = textCol;

  return (
    <section data-cy="two-column-module" {...props}>
      <LayoutGrid className={cx(layoutGridClassName ?? 'gap-y-12')}>
        <div
          className={twoColTextBlockVar({ smBreakTextPos, mdBreakTextPos })}
          data-cy="text-block"
        >
          {titleProps && (
            <TypographyV3
              {...titleProps}
              data-cy="text-block-title"
              Element="h2"
            >
              {t(titleProps.label)}
            </TypographyV3>
          )}
          <div
            className={cx(textCol.textColClassName ?? 'flex flex-col gap-8')}
          >
            {textCol.paragraphs.map((paragraph, index) => {
              const { textProps, subtextProps, icon } = paragraph;
              const ParaIcon = icon;
              return (
                <Motion
                  key={textProps.label}
                  className={cx(
                    textCol.iconTextContainerClassName ?? 'flex flex-row gap-4',
                  )}
                  initial={{ opacity: 0 }}
                  transition={{
                    delay: 0.1 * index,
                    duration: 0.75,
                    type: 'tween',
                  }}
                  viewport={{ amount: 0.66, once: true }}
                  whileInView={{ opacity: 1 }}
                  data-cy="icon-text-container"
                >
                  {ParaIcon && (
                    <div
                      data-cy="text-block-icon"
                      className={paragraph.iconClassName}
                    >
                      <ParaIcon
                        width={textCol.iconSize ?? 32}
                        height={textCol.iconSize ?? 32}
                      />
                    </div>
                  )}
                  <div data-cy="two-col-text-li">
                    <TypographyV3 {...textProps} Element="h3">
                      {t(textProps.label)}
                    </TypographyV3>
                    {subtextProps && (
                      <TypographyV3 {...subtextProps}>
                        {t(subtextProps.label)}
                      </TypographyV3>
                    )}
                  </div>
                </Motion>
              );
            })}
            {buttonProps && (
              <ButtonV3 {...buttonProps} data-cy="two-col-shop-cta">
                {t(buttonProps.label)}
              </ButtonV3>
            )}
          </div>
        </div>
        <div
          className={twoColImageBlockVar({ smBreakTextPos, mdBreakTextPos })}
          data-cy="image-block"
        >
          <div className="relative aspect-square overflow-hidden rounded-lg xl:aspect-5/4">
            <Motion {...imageCol.motionProps}>
              <Image
                className="rounded-lg object-cover"
                localizedAlt={imageCol.localizedAlt}
                src={imageCol.src}
                fill
                sizes="(max-width: 1023px) 90vw, (max-width: 1567px) 50vw, 785px"
              />
            </Motion>
          </div>
        </div>
      </LayoutGrid>
    </section>
  );
};

export default TwoColumnModule;
